import { useEffect, useState, useCallback } from "react";

interface CacheStatus {
  isRegistered: boolean;
  isActive: boolean;
  itemCount: number;
  totalSizeMB: string;
  error?: string;
}

export function useBgRemovalCache() {
  const [cacheStatus, setCacheStatus] = useState<CacheStatus>({
    isRegistered: false,
    isActive: false,
    itemCount: 0,
    totalSizeMB: "0"
  });

  const getCacheStatusFromSW = useCallback(async (sw: ServiceWorker) => {
    try {
      const messageChannel = new MessageChannel();
      const statusPromise = new Promise<CacheStatus>((resolve) => {
        messageChannel.port1.onmessage = (event) => {
          const data = event.data;
          resolve({
            isRegistered: true,
            isActive: true,
            itemCount: data.itemCount || 0,
            totalSizeMB: data.totalSizeMB || "0"
          });
        };
      });

      sw.postMessage({ type: "GET_CACHE_STATUS" }, [messageChannel.port2]);
      const status = await statusPromise;
      setCacheStatus(status);
    } catch {
      // Failed to get cache status
    }
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      setCacheStatus((prev) => ({ ...prev, error: "Service workers not supported" }));
      return;
    }

    const registerServiceWorker = async () => {
      try {
        const registration = await navigator.serviceWorker.register("/bg-removal-sw.js", {
          scope: "/"
        });

        const isActive = !!registration.active;
        setCacheStatus((prev) => ({ ...prev, isRegistered: true, isActive }));

        if (registration.active) {
          getCacheStatusFromSW(registration.active);
        } else if (registration.installing) {
          registration.installing.addEventListener("statechange", (e) => {
            const sw = e.target as ServiceWorker;
            if (sw.state === "activated") {
              setCacheStatus((prev) => ({ ...prev, isActive: true }));
              getCacheStatusFromSW(sw);
            }
          });
        }
      } catch (error) {
        setCacheStatus((prev) => ({
          ...prev,
          error: error instanceof Error ? error.message : "Registration failed"
        }));
      }
    };

    registerServiceWorker();
  }, [getCacheStatusFromSW]);

  const refreshStatus = useCallback(async () => {
    const registration = await navigator.serviceWorker.getRegistration("/");
    if (registration?.active) {
      await getCacheStatusFromSW(registration.active);
    }
  }, [getCacheStatusFromSW]);

  const clearCache = useCallback(async () => {
    const registration = await navigator.serviceWorker.getRegistration("/");
    if (!registration?.active) {
      throw new Error("Service worker not active");
    }

    const messageChannel = new MessageChannel();
    const clearPromise = new Promise<void>((resolve, reject) => {
      messageChannel.port1.onmessage = (event) => {
        if (event.data.success) {
          resolve();
        } else {
          reject(new Error("Failed to clear cache"));
        }
      };
    });

    registration.active.postMessage({ type: "CLEAR_CACHE" }, [messageChannel.port2]);
    await clearPromise;
    await refreshStatus();
  }, [refreshStatus]);

  return { cacheStatus, refreshStatus, clearCache };
}

export async function registerBgRemovalCacheWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;

  try {
    return await navigator.serviceWorker.register("/bg-removal-sw.js", { scope: "/" });
  } catch {
    return null;
  }
}
